/**
 * This module is to be used inside the templating engine. The porpuse is to be initialized beofre being used and then
 * pass the translate functioanlity to the vam. At the moment is managing translations and rich elements inside the
 * translations like HTML elements and date operations.
 *
 * The current format for reach data is {{OPERATOR||DATA||DATA}} you can find examples in the translations.js file
 * within sportsbook api.
 *
 * Current Operators:
 * {{HTML||<label>||text||atribbute||value||attribute||value...}}
 * {{date||fullYear}}
 */
import _ from 'lodash';

var i18n = {
    dict: {},
    showTranslationsTip: false,
    locale: 'en-gb',
    englishLocale: 'en-gb',
    defaultLocale: 'en-gb',
};

/**
 * Checks if a translation from the dictionary contains rich elements to be processed
 *
 * @param   {String}    translatedKey String from the dictionary before being processed
 *
 * @param   {object}    params Object containing params to replace inside the dictionary translation
 *
 * @returns {String}    final translation in string
 *
 * @private
 */
var processTranslation = function processTranslation(translatedKey, params) {

    var operators = translatedKey.match(/{{([^{}]+)}}/gi);

    _.each(operators, function (operator) {

        var cleanContent = operator.replace('{{', '').replace('}}', '');

        translatedKey = translatedKey.replace(operator, operatorProcessor(cleanContent, params));
    });

    return appendTranslationsTip(translatedKey);

};

/**
 * This uses a simple replacement mechanisim. Inside the translation we have a placeholder and we pass
 * and object as a parameter (underscore style).
 * I.E: key: 'this is a translation for a {{sport}}', vam.translate('key',{sport:'tennis'});
 *
 * @param   {Object} objectOperator Contains a rich element from the dictionary translation.
 *
 * @param   {object} params Object containing params to replace inside the dictionary translation
 *
 * @returns {String} final translated operator result
 *
 * @private
 */
var simpleReplaceProcessor = function simpleReplaceProcessor(objectOperator, params) {

    if (objectOperator.length === 1 && params && params.hasOwnProperty(objectOperator[0])) {

        return params[objectOperator[0]].toString();

    } else {

        return '';
    }
};

/**
 * Takes an operator (rich element) and converts it into a final string
 *
 * @param   {String} operator   Contains a rich element from the dictionary translation.
 *
 * @param   {object} params     Object containing params to replace inside the dictionary translation
 *
 * @returns {String} final translated operator result
 *
 * @private
 */
var operatorProcessor = function operatorProcessor(operator, params) {


    var objectOperator = operator.split('||');

    if (objectOperator.length > 1 && operations.hasOwnProperty(objectOperator[0])) {

        return operations[objectOperator[0]](objectOperator.slice(1, objectOperator.length));

    } else {

        return simpleReplaceProcessor(objectOperator, params);
    }

};

/**
 * Decode slug
 *
 * @param   {String} slug slug to be decoded
 *
 * @returns {string}      decoded slug
 *
 * @private
 */
var decodeSlug = function decodeSlug(slug) {

    return slug ? window.decodeURI(slug) : slug;
};

/**
 * Contains all the rich element operator processors
 *
 * @type {Object}
 *
 * @private
 */
var operations = {

    /**
     * Processes a label type operator. Creates HTML elements, based on label, anchor and attributes
     *
     * @param {Array} operatorData Contains the data needed to process the operator
     *
     * @returns {String} operator converted into final generated string
     *
     * @private
     */
    HTML: function labelOperation(operatorData) {


        var data = {
            label: operatorData[0].toString(),
            text: operatorData[1].toString(),
            attrs: operatorData.slice(2, operatorData.length),
        };


        var result = ['<' + data.label];

        _.each(data.attrs, function (value, i) {

            if (Math.abs(i % 2) === 1) {

                result.push(' ' + data.attrs[i - 1] + '="' + value + '"');
            }


        });

        result.push('>' + data.text + '</' + data.label + '>');

        return result.join('');
    },

    /**
     * Processes a date type operator. Provides inline date functionality
     *
     * @param {Array} operatorData Contains the data needed to process the operator
     *
     * @returns {String} operator converted into final generated string
     *
     * @private
     */
    date: function dateOpeartion(operatorData) {

        var dateOperations = {

            fullYear: function fullYear() {
                return new Date().getFullYear();
            },
        };

        return (dateOperations.hasOwnProperty(operatorData[0])) ? dateOperations[operatorData[0]] : '';
    },
};

/**
 * If configuration is set to true ( a query parameter set on server.js ) then we wrap the
 * translation into special characters to make it easy for anyone to spot them
 *
 * @param   {String}    translatedString final translated string
 *
 * @returns {String}    translated string that could be wrapped
 *
 * @private
 */
var appendTranslationsTip = function appendTranslationsTip(translatedString) {

    return (i18n.showTranslationsTip) ? ('_F#' + translatedString + '#F_') : translatedString;

};

/**
 * Appends a query param to a given url
 *
 * @param   {String}    requestURL  Base URL
 * @param   {String}    queryParam  Query param
 *
 * @returns {String}
 *
 * @private
 */
var appendQueryParam = function apendQueryParam(requestURL, queryParam) {

    var operator = '?';

    if (requestURL.indexOf(operator) > -1) {

        operator = '&';
    }

    return requestURL + operator + queryParam;

};

/**
 * Replace translatable parts in the url
 *
 * @param   {String} beg       First part of the url (eg. /betting/en-gb/)
 * @param   {String} url       Second part of the url (eg. football/OB_EV123/name)
 * @param   {String} direction The direction of translation (to/from English)
 *
 * @returns {String}           Translated url
 *
 * @private
 */
var replaceUrlParts = function replaceUrlParts(beg, url, direction) {

    const splitUrl = url.split('?');
    const pathname = splitUrl[0];
    const query = splitUrl[1];
    const splitPathname = pathname.split('/');

    if (i18n.urlDict) {
        const dict = direction === 'from' ? i18n.urlDict.engToLocal : i18n.urlDict.localToEng;

        _.each(splitPathname, function (part, index) {

            part = part.toLocaleLowerCase();
            if (dict.hasOwnProperty(part)) {

                splitPathname[index] = dict[part];
            }
        });
    }

    return _.compact([beg + splitPathname.join('/'), query]).join('?');
};

/**
 * Make a replacement
 *
 * @param   {String} url       Url to be translated
 * @param   {String} direction The direction of translation (to/from English)
 *
 * @returns {String}
 *
 * @private
 */
var matchAndTranslate = function matchAndTranslate(url, direction) {

    var match = url.match(new RegExp('^(.*[^/]+/' + api.getLocale() + '/)(.*)$'));

    if (match) {

        url = replaceUrlParts(match[1], match[2], direction);
    }

    return url;
};

/**
 * Flat object to contain public methods
 *
 * @type {Object}
 *
 * @public
 */
var api = {

    /**
     * Translates a given key. While translating processes the rich elements as operators to compose the
     * final string.
     *
     * @param   {String}    key     Key to look for in the dictionary
     *
     * @param   {object}    params  Object containing params to replace inside the dictionary translation
     *
     * @returns {String}    key     Key to check into the dictionary
     *
     * @public
     */
    translate: function translate(key, params) {

        return processTranslation(i18n.dict[key] || '', params);
    },

    /**
     * Check if there is translation for specified key
     *
     * @param   {String}    key     Key to look for in the dictionary
     *
     * @returns {Boolean}           true if there is translation for the key
     *
     * @public
     */
    hasTranslation: function hasTranslation(key) {

        return i18n.dict.hasOwnProperty(key);
    },

    /**
     * Translate sport slug to english
     *
     * @param   {String}    key     Key to look for in the dictionary
     *
     * @param   {object}    params  Object containing params to replace inside the dictionary translation
     *
     * @returns {String}    key     Key to check into the dictionary
     *
     * @public
     */
    translateSlugToEn: function translateSlugToEn(slug) {

        var decodedSlug = decodeSlug(slug);
        return (i18n && i18n.slugDict && i18n.slugDict.slugs && i18n.slugDict.slugs[decodedSlug]) || decodedSlug;
    },

    /**
     * Translate slugEn sport slug to locale
     *
     * @param   {String}    key     Key to look for in the dictionary
     *
     * @param   {object}    params  Object containing params to replace inside the dictionary translation
     *
     * @returns {String}    key     Key to check into the dictionary
     *
     * @public
     */
    translateSlug: function translateSlug(slugEn) {

        return (i18n && i18n.slugDict && i18n.slugDict.slugsRev && i18n.slugDict.slugsRev[slugEn]) || slugEn;
    },


    /**
     * Translates URL according to the direction ('to' English or 'from' English)
     *
     * @param   {String} url       Url to be translated
     * @param   {String} direction The direction of translation (to/from English)
     *
     * @returns {String}
     *
     * @private
     */
    translateUrl: function translateUrl(url, direction) {

        if (url) {

            url = matchAndTranslate(decodeURI(url), direction);
        }

        return url;
    },

    /**
     * Returns the translations tip flag
     *
     * @returns {Boolean}
     *
     * @public
     */
    getShowTranslationsTip: function getShowTranslationsTip() {

        return i18n.showTranslationsTip;
    },

    /**
     * Render the template based on the adaptor name
     *
     * @returns     {String}    locale i.e 'en-gb'
     *
     * @public
     */
    getLocale: function getLocale() {

        return i18n.locale;
    },

    /**
     * Returns English locale code
     *
     * @returns {String}
     *
     * @public
     */
    getEnglishLocale: function getEnglishLocale() {

        return i18n.englishLocale;
    },

    /**
     * Returns default locale code
     *
     * @returns {String}
     *
     * @public
     */
    getDefaultLocale: function getDefaultLocale() {

        return i18n.defaultLocale;
    },

    /**
     * Function to add a the translationTip in case we are using it in the url
     *
     * @param   {String}    requestURL  Base URL
     *
     * @private
     */
    addTranslationTipToURL: function addTranslationTipToURL(requestURL) {


        return (i18n.showTranslationsTip) ?
            appendQueryParam(requestURL, 'showTranslationsTip') :
            requestURL;

    },

    /**
     * This function initializes this module with dict data
     *
     * @returns {void}
     *
     * @private
     */
    init: function init() {

        _.extend(i18n, window.WH.i18n);

        window.translator = api;
    },
};

export default api;
